import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getCategoryFields from './getCategoryFields.graphql';
import saveCategoryFieldM from './saveCategoryField.graphql';
import deleteCategoryFieldM from './deleteCategoryField.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';
const TEAL = '#0E9488';

const INPUT_TYPES = [
  { v: 'text', l: 'Text' },
  { v: 'number', l: 'Number' },
  { v: 'select', l: 'Select (single)' },
  { v: 'multiselect', l: 'Multi-select' },
  { v: 'boolean', l: 'Yes / No' },
];

// M12 (U6.1): admin Category Studio "Questions" manager. CRUD the per-category / per-subcategory
// dynamic questionnaire fields (CategoryField) that the client app renders at booking.
class CategoryQuestions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      categoryId: '', subCategoryId: '', loaded: false, busy: false, message: '',
      // new/edit field form
      editId: null, fieldKey: '', label: '', inputType: 'text', options: '', isRequired: false, helpText: '', displayOrder: 0,
    };
  }

  load = async () => {
    const categoryId = parseInt(this.state.categoryId, 10);
    if (!categoryId) { this.setState({ message: 'Enter a Category ID.' }); return; }
    this.setState({ busy: true, message: '' });
    try {
      await this.props.fields.refetch({
        categoryId,
        subCategoryId: this.state.subCategoryId ? parseInt(this.state.subCategoryId, 10) : null,
      });
      this.setState({ busy: false, loaded: true });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  edit = (f) => this.setState({
    editId: f.id, fieldKey: f.fieldKey || '', label: f.label || '', inputType: f.inputType || 'text',
    options: '', isRequired: !!f.isRequired, helpText: f.helpText || '', displayOrder: f.displayOrder || 0,
  });

  resetForm = () => this.setState({ editId: null, fieldKey: '', label: '', inputType: 'text', options: '', isRequired: false, helpText: '', displayOrder: 0 });

  save = async () => {
    if (!this.state.fieldKey || !this.state.label) { this.setState({ message: 'Key and label are required.' }); return; }
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.saveMut({ variables: {
        id: this.state.editId, categoryId: parseInt(this.state.categoryId, 10),
        subCategoryId: this.state.subCategoryId ? parseInt(this.state.subCategoryId, 10) : null,
        fieldKey: this.state.fieldKey, label: this.state.label, inputType: this.state.inputType,
        options: this.state.options || null, isRequired: this.state.isRequired, helpText: this.state.helpText || null,
        displayOrder: parseInt(this.state.displayOrder, 10) || 0, isActive: true,
      } });
      const r = res && res.data && res.data.saveCategoryField;
      if (r && r.status === 200) { this.resetForm(); this.setState({ busy: false, message: '✅ Saved.' }); this.props.fields.refetch(); }
      else this.setState({ busy: false, message: (r && r.errorMessage) || 'Save failed.' });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  remove = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    this.setState({ busy: true, message: '' });
    try {
      await this.props.deleteMut({ variables: { id } });
      this.setState({ busy: false, message: '✅ Deleted.' });
      this.props.fields.refetch();
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  render() {
    const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginRight: 8 };
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 16 };
    const th = { padding: 10, textAlign: 'left' };
    const td = { padding: 10, borderTop: '1px solid #f0f0f0', fontSize: 13 };

    const fields = (this.props.fields && this.props.fields.getCategoryFields && this.props.fields.getCategoryFields.results) || [];

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">Category Questions</h1>
        {this.state.message ? <div style={{ marginBottom: 12, color: this.state.message[0] === '✅' ? TEAL : RED, fontWeight: 600 }}>{this.state.message}</div> : null}

        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Pick a category</div>
          <input style={{ ...input, width: 160 }} placeholder="Category ID" value={this.state.categoryId}
                 onChange={(e) => this.setState({ categoryId: e.target.value.replace(/[^0-9]/g, '') })} />
          <input style={{ ...input, width: 200 }} placeholder="Sub-category ID (optional)" value={this.state.subCategoryId}
                 onChange={(e) => this.setState({ subCategoryId: e.target.value.replace(/[^0-9]/g, '') })} />
          <button style={btn(INK)} disabled={this.state.busy} onClick={this.load}>Load questions</button>
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Leave sub-category empty for category-wide questions. (IDs from the category list — a picker comes later.)</div>
        </div>

        {this.state.loaded && (
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>{this.state.editId ? 'Edit question' : 'Add question'}</div>
            <input style={{ ...input, width: 180 }} placeholder="Key (e.g. tv_size)" value={this.state.fieldKey}
                   onChange={(e) => this.setState({ fieldKey: e.target.value.trim() })} />
            <input style={{ ...input, width: 240 }} placeholder="Label (e.g. TV size?)" value={this.state.label}
                   onChange={(e) => this.setState({ label: e.target.value })} />
            <select style={input} value={this.state.inputType} onChange={(e) => this.setState({ inputType: e.target.value })}>
              {INPUT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <label style={{ marginRight: 8, fontSize: 13 }}>
              <input type="checkbox" checked={this.state.isRequired} onChange={(e) => this.setState({ isRequired: e.target.checked })} /> Required
            </label>
            <input style={{ ...input, width: 70 }} placeholder="Order" value={this.state.displayOrder}
                   onChange={(e) => this.setState({ displayOrder: e.target.value.replace(/[^0-9]/g, '') })} />
            <div style={{ marginTop: 8 }}>
              <input style={{ ...input, width: 360 }} placeholder="Help text (optional)" value={this.state.helpText}
                     onChange={(e) => this.setState({ helpText: e.target.value })} />
              {(this.state.inputType === 'select' || this.state.inputType === 'multiselect') && (
                <input style={{ ...input, width: 360 }} placeholder='Options JSON e.g. ["Drywall","Brick"]' value={this.state.options}
                       onChange={(e) => this.setState({ options: e.target.value })} />
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              <button style={btn(RED)} disabled={this.state.busy} onClick={this.save}>{this.state.editId ? 'Update' : 'Add'}</button>
              {this.state.editId ? <button style={{ ...btn('#fff'), color: INK, border: '1px solid #ddd' }} onClick={this.resetForm}>Cancel</button> : null}
            </div>
          </div>
        )}

        {this.state.loaded && (
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', fontWeight: 700, borderBottom: '1px solid #f0f0f0' }}>Questions</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ background: '#fafafa' }}><th style={th}>#</th><th style={th}>Key</th><th style={th}>Label</th><th style={th}>Type</th><th style={th}>Required</th><th style={th}>Order</th><th style={th}></th></tr></thead>
              <tbody>
                {fields.length === 0 && <tr><td style={td} colSpan={7}>No questions yet for this category.</td></tr>}
                {fields.map((f) => (
                  <tr key={f.id}>
                    <td style={td}>{f.id}</td><td style={td}>{f.fieldKey}</td><td style={td}>{f.label}</td>
                    <td style={td}>{f.inputType}</td><td style={td}>{f.isRequired ? 'Yes' : 'No'}</td><td style={td}>{f.displayOrder}</td>
                    <td style={td}>
                      <button style={{ ...btn(INK), padding: '4px 10px', marginRight: 6 }} onClick={() => this.edit(f)}>Edit</button>
                      <button style={{ ...btn(RED), padding: '4px 10px' }} onClick={() => this.remove(f.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  withQuery(getCategoryFields, { name: 'fields', options: { variables: { categoryId: 0 }, fetchPolicy: 'network-only', ssr: false } }),
  withMutation(saveCategoryFieldM, { name: 'saveMut' }),
  withMutation(deleteCategoryFieldM, { name: 'deleteMut' }),
)(CategoryQuestions);
